import { useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Title2,
  Caption1,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
} from '@fluentui/react-components';
import {
  NavigationRegular,
  DismissRegular,
  BoardRegular,
  PersonRegular,
  CallRegular,
  BranchRegular,
  BuildingRegular,
  PeopleQueueRegular,
  SettingsRegular,
  MicRegular,
} from '@fluentui/react-icons';
import type { ReactElement, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { AppNav } from './AppNav';
import { useAuth } from '../auth/TeamsAuthProvider';
import { customerBranding, buildNavItems, hasEmulationAccess } from '../../utils/access';
import { useChartColors } from './charts/chartTheme';

// The one PageHeader for every route (spec §4.1, amended 2026-09-12: no
// `variant` prop, no hero -- see the amendment note at the top of
// docs/superpowers/specs/2026-09-11-voiceiq-visual-system-design.md). Wired
// on every route; SlimBrandHeader, company/BrandHeader.tsx and
// branch/BranchBrandHeader.tsx are deleted.

// Drawer nav icons. Not shared with AppNav's desktop button row (which reads
// icons from its own map) -- both ultimately read item visibility from
// utils/access.ts#buildNavItems, so the two can't disagree on which items
// exist, only on how each is drawn.
const NAV_ICONS: Record<string, ReactElement> = {
  '/dashboard': <BoardRegular />,
  '/my': <PersonRegular />,
  '/calls': <CallRegular />,
  '/branch': <BranchRegular />,
  '/recordings': <MicRegular />,
  '/company': <BuildingRegular />,
  '/queues': <PeopleQueueRegular />,
  '/settings': <SettingsRegular />,
};

const useStyles = makeStyles({
  bar: {
    position: 'relative',
    height: '44px',
    background: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow4,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 20px 0 12px',
  },
  stripe: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
  },
  menuButton: {
    minWidth: '28px',
    width: '28px',
    height: '28px',
    padding: '0',
    flexShrink: 0,
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1,
    minWidth: 0,
  },
  plate: {
    background: '#FFFFFF',
    borderRadius: tokens.borderRadiusMedium,
    padding: '4px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  plateImg: {
    display: 'block',
    maxHeight: '24px',
    maxWidth: '120px',
    objectFit: 'contain',
  },
  customerName: {
    fontSize: '13px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
    letterSpacing: '-0.1px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexShrink: 0,
  },
  sep: {
    color: tokens.colorNeutralStroke1,
    fontSize: '12px',
    userSelect: 'none',
    flexShrink: 0,
  },
  crumbCurrent: {
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  barRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
    padding: '14px 24px 12px',
    '@media (max-width: 767px)': {
      padding: '14px 16px 12px',
    },
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  navSlot: {
    display: 'flex',
    alignItems: 'center',
  },
  // Drawer nav styles.
  navList: {
    listStyleType: 'none',
    margin: 0,
    padding: '8px 0',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: '14px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground1,
    transition: 'background 0.15s ease',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    transition: 'background 0.15s ease',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  navIcon: {
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
});

export interface PageHeaderProps {
  /** Page title, `Title2`. Always present (spec §3.2). */
  title: string;
  /** Scope caption under the title, `Caption1`, plain text (no pills). */
  subtitle?: string;
  /** Current-page breadcrumb segment, shown in the teal highlight color. */
  breadcrumb?: string;
  /** Reserved for a future per-page meta node; unused by this PR's callers. */
  meta?: ReactNode;
  /**
   * Content for the bar's actions slot (e.g. the "View As" button). Rendered
   * only for platform and tenant admins (spec §4.1) -- PageHeader makes that
   * call itself via `hasEmulationAccess`, so callers don't have to repeat the
   * gate at every call site.
   */
  actions?: ReactNode;
}

export function PageHeader({ title, subtitle, breadcrumb, actions }: PageHeaderProps) {
  const styles = useStyles();
  const { user, themeName } = useAuth();
  const colors = useChartColors();
  const logoTheme = themeName === 'dark' || themeName === 'contrast' ? 'dark' : 'light';
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navItems = buildNavItems(user);
  const { displayName, logoDataUrl } = customerBranding(user);
  const canShowActions = hasEmulationAccess(user);

  const handleNavClick = useCallback(
    (path: string) => {
      navigate(path);
      setDrawerOpen(false);
    },
    [navigate],
  );

  const isActive = useCallback(
    (path: string) => {
      if (path === '/calls') {
        return location.pathname === '/calls' || location.pathname.startsWith('/calls/');
      }
      return location.pathname === path;
    },
    [location.pathname],
  );

  return (
    <>
      <div className={styles.bar}>
        <span className={styles.stripe} style={{ background: colors.outbound }} />
        <Button
          className={styles.menuButton}
          appearance="subtle"
          icon={<NavigationRegular />}
          aria-label="Open navigation menu"
          onClick={() => setDrawerOpen(true)}
        />
        <div className={styles.identity}>
          {logoDataUrl ? (
            <span className={styles.plate}>
              <img src={logoDataUrl} alt={displayName} className={styles.plateImg} />
            </span>
          ) : displayName ? (
            <span className={styles.customerName}>{displayName}</span>
          ) : null}
          {breadcrumb && (logoDataUrl || displayName) && <span className={styles.sep}>/</span>}
          {breadcrumb && (
            <span className={styles.crumbCurrent} style={{ color: colors.outbound }}>
              {breadcrumb}
            </span>
          )}
        </div>
        <div className={styles.barRight}>
          {canShowActions ? actions : null}
          <Logo variant="wordmark" theme={logoTheme} height={26} />
        </div>
      </div>

      <div className={styles.titleRow}>
        <div className={styles.titleBlock}>
          <Title2 as="h1">{title}</Title2>
          {subtitle && (
            <Caption1 className={styles.subtitle}>{subtitle}</Caption1>
          )}
        </div>
        <div className={styles.navSlot}>
          <AppNav />
        </div>
      </div>

      <Drawer
        type="overlay"
        separator
        open={drawerOpen}
        onOpenChange={(_e, { open }) => setDrawerOpen(open)}
        position="start"
        size="small"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close navigation"
                icon={<DismissRegular />}
                onClick={() => setDrawerOpen(false)}
              />
            }
          >
            <Logo variant="wordmark" theme={logoTheme} height={26} />
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <ul className={styles.navList}>
            {navItems.map((item) => (
              <li key={item.path}>
                <button
                  className={isActive(item.path) ? styles.navItemActive : styles.navItem}
                  onClick={() => handleNavClick(item.path)}
                >
                  <span className={styles.navIcon}>{NAV_ICONS[item.path]}</span>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </DrawerBody>
      </Drawer>
    </>
  );
}
